/**
 * This tab's record of an idle logout: one `sessionStorage` entry, `idleLogout`.
 *
 * It holds nothing readable about the user or the page: a random handle naming the server's
 * record, the address encrypted with a key the tab no longer has, an opaque group, and two
 * timestamps. Only the server can decrypt the address, and only for the same user once they have
 * logged in again.
 *
 * It's `sessionStorage` because each tab needs its own entry; several tabs time out together. The
 * entry survives the round trip to the identity provider, because the tab comes back to the same
 * origin. None of it goes to `localStorage` or cookies.
 */
import type { SealedPlace } from '@auth/resume/resumeCredentials'

const STORAGE_KEY = 'idleLogout'

/**
 * Written in plain text by the CRPM-3963 release: the saved address and a timeout notice. Deleted
 * whenever the app starts and by the Resume page.
 */
const LEGACY_STORAGE_KEYS = ['postLogoutRedirectTo', 'sessionExpiryNotice'] as const

/**
 * How long a saved place can be restored when the server didn't say: the default of the BFF's
 * `crpm.auth.resume.window`. The server sends its setting with the credentials, and keeps its
 * record a few minutes longer than that, so a tab says Expired before the server forgets.
 */
export const DEFAULT_RESUME_WINDOW_MS = 24 * 60 * 60 * 1000

/** The longest window a tab accepts: the most `crpm.auth.resume.window` may be set to. */
export const MAX_RESUME_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

const VERSION = 2

// Base64url with its padding, as the server's ResumeFormatUtil checks it.
const HANDLE = /^[A-Za-z0-9_-]{43}=$/
const GROUP = /^[A-Za-z0-9_-]{22}==$/
const IV = /^[A-Za-z0-9_-]{16}$/
/** 24 to 400 characters in groups of four, padding only in the last. */
const CIPHERTEXT =
  /^(?:[A-Za-z0-9_-]{4}){5,99}(?:[A-Za-z0-9_-]{4}|[A-Za-z0-9_-]{3}=|[A-Za-z0-9_-]{2}==)$/

export interface IdleLogoutEntry {
  v: typeof VERSION
  /** Absent when no place could be saved, and once the entry has expired. */
  handle?: string
  /** Present with `handle`. */
  group?: string
  /**
   * The initialization vector (IV/nonce) the address was encrypted with. Present together with
   * `ct`, and only when the address could be saved.
   */
  iv?: string
  /** The ciphertext: the encrypted address. */
  ct?: string
  loggedOutAt: number
  expiresAt: number
}

/** Saves this tab's place. Without one, the entry still records that this tab timed out. */
export function writeIdleLogoutEntry(place: SealedPlace | undefined, now = Date.now()) {
  const entry: IdleLogoutEntry = {
    v: VERSION,
    ...(place && { handle: place.handle, group: place.group }),
    ...(place?.iv && place.ct && { iv: place.iv, ct: place.ct }),
    loggedOutAt: now,
    expiresAt: now + Math.min(place?.windowMs ?? DEFAULT_RESUME_WINDOW_MS, MAX_RESUME_WINDOW_MS),
  }
  write(entry)
}

/**
 * This tab's entry, or undefined. Once it has expired, everything but the timestamps is deleted,
 * so a reloaded tab can still say it expired.
 *
 * The entry is same-origin writable, so it's checked like any other input. An entry that fails the
 * check is deleted.
 */
export function readIdleLogoutEntry(now = Date.now()): IdleLogoutEntry | undefined {
  const raw = readRaw()
  if (raw === null) {
    return undefined
  }
  const entry = parse(raw)
  if (!entry) {
    deleteIdleLogoutEntry()
    return undefined
  }
  if (now >= entry.expiresAt && entry.handle !== undefined) {
    const trimmed: IdleLogoutEntry = {
      v: VERSION,
      loggedOutAt: entry.loggedOutAt,
      expiresAt: entry.expiresAt,
    }
    write(trimmed)
    return trimmed
  }
  return entry
}

export function deleteIdleLogoutEntry() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable (private browsing, quota). There's nothing to delete.
  }
}

/** Deletes the plain-text keys an earlier release wrote. */
export function deleteLegacyIdleLogoutKeys() {
  try {
    for (const key of LEGACY_STORAGE_KEYS) {
      window.sessionStorage.removeItem(key)
    }
  } catch {
    // See deleteIdleLogoutEntry.
  }
}

function write(entry: IdleLogoutEntry) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch {
    // Storage unavailable. Losing this tab's place isn't worth failing the logout.
  }
}

function readRaw(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function parse(raw: string): IdleLogoutEntry | undefined {
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return undefined
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined
  }
  const { v, handle, group, iv, ct, loggedOutAt, expiresAt } = value as Record<string, unknown>
  if (
    v !== VERSION ||
    !isTimestamp(loggedOutAt) ||
    !isTimestamp(expiresAt) ||
    expiresAt < loggedOutAt ||
    expiresAt > loggedOutAt + MAX_RESUME_WINDOW_MS ||
    !optionalMatch(handle, HANDLE) ||
    !optionalMatch(group, GROUP) ||
    !optionalMatch(iv, IV) ||
    !optionalMatch(ct, CIPHERTEXT) ||
    (handle === undefined) !== (group === undefined) ||
    (iv === undefined) !== (ct === undefined) ||
    (iv !== undefined && handle === undefined)
  ) {
    return undefined
  }
  return {
    v: VERSION,
    ...(handle !== undefined && { handle, group }),
    ...(iv !== undefined && { iv, ct }),
    loggedOutAt,
    expiresAt,
  } as IdleLogoutEntry
}

function isTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function optionalMatch(value: unknown, pattern: RegExp): boolean {
  return value === undefined || (typeof value === 'string' && pattern.test(value))
}
